import {
  layoutComponentIds,
  resolveLayoutItems,
} from '@shared/scenes/workflows/resolveViewTemplateLayout';
import {
  AnyLayoutItem,
  ViewTemplateLayoutImage,
  ViewTemplateLayoutText,
  ViewTemplateLayoutVideo,
  isLayoutImageItem,
  isLayoutTextItem,
  isLayoutVideoItem,
} from '@shared/scenes/workflows/viewTemplateLayoutItems';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

/**
 * One renderable slot in a subform, resolved from its view template layout.
 *
 * A layout holds fields, sections, and the text and media content items — see
 * `viewTemplateLayoutItems`, which owns the guards and accessors for the latter.
 */
export interface SubformComponentEntry {
  kind: 'component';
  /** Stable across renders, and unique among entries. */
  key: string;
  component: API.AnyComponent;
}

export interface SubformTextEntry {
  kind: 'text';
  key: string;
  item: ViewTemplateLayoutText;
}

export interface SubformImageEntry {
  kind: 'image';
  key: string;
  item: ViewTemplateLayoutImage;
}

export interface SubformVideoEntry {
  kind: 'video';
  key: string;
  item: ViewTemplateLayoutVideo;
}

export type SubformLayoutEntry =
  | SubformComponentEntry
  | SubformTextEntry
  | SubformImageEntry
  | SubformVideoEntry;

export function isSubformComponentEntry(entry: SubformLayoutEntry): entry is SubformComponentEntry {
  return entry.kind === 'component';
}

/**
 * The subform's slots in layout order.
 *
 * Sections are flattened: a subform cannot be given one in the builder, but a layout could
 * still carry one, and skipping it would drop its fields out of the form. Everything else —
 * stale ids, duplicates, empty content items, and fields the layout never mentions — follows the
 * rules every layout reader shares (see `resolveLayoutItems`).
 */
export function buildSubformLayoutEntries(
  components: API.AnyComponent[],
  layout?: API.ViewTemplateLayoutFragment | null,
): SubformLayoutEntry[] {
  const componentsById = new Map(components.map(component => [component.id, component]));

  // Content items have no component to key on and their own id is optional, so position
  // stands in. Ordinal rather than array index, so a key survives a field being removed
  // from somewhere else in the list.
  let contentItemCount = 0;
  const contentKey = (item: AnyLayoutItem) => {
    contentItemCount += 1;
    return `${item.type}-${item.id ?? contentItemCount}`;
  };

  return resolveLayoutItems(components, layout).flatMap((item): SubformLayoutEntry[] => {
    if (isLayoutTextItem(item)) {
      return [{ kind: 'text', key: contentKey(item), item }];
    }
    if (isLayoutImageItem(item)) {
      return [{ kind: 'image', key: contentKey(item), item }];
    }
    if (isLayoutVideoItem(item)) {
      return [{ kind: 'video', key: contentKey(item), item }];
    }
    return layoutComponentIds(item)
      .map(componentId => componentsById.get(componentId))
      .filter(notNil)
      .map(component => ({ kind: 'component', key: component.id, component }));
  });
}
